// Инвентарь и экипировка — tech/03 §4 (прототип: E05-T02 «инвентарь, экипировка, сеты, улучшение, продажа»).
import { z } from 'zod';
import { CurrencyCode, IsoTime, ItemDefId, NextCursor, Rarity, Slot, StackDefId, strict } from '../common.js';
import { endpoint } from '../endpoint.js';
import { DerivedStats, Item, ItemStats, SetBonus, Stack, Wallet } from './models.js';

const ItemIdStr = z.string().regex(/^[1-9][0-9]{0,18}$/);
const ItemIds = z.array(ItemIdStr).min(1).max(50);

/** В спецификации у ответа нет `nextCursor`, хотя запрос принимает `cursor` — добавлено (Р-02). */
export const getInventory = endpoint({
  id: 'inventory.list', method: 'GET', path: '/inventory', auth: true, signed: false, idempotency: 'read', rate: 'read',
  query: strict({ slot: Slot.optional(), cursor: z.string().max(256).optional() }),
  response: strict({ items: z.array(Item), stacks: z.array(Stack), setBonuses: z.array(SetBonus), nextCursor: NextCursor }),
  status: 200, errors: [], spec: 'tech/03 §4', task: 'E05-T02',
});

const EquipResponse = strict({ equipped: z.array(Item), stats: DerivedStats });

export const equip = endpoint({
  id: 'inventory.equip', method: 'POST', path: '/inventory/equip', auth: true, signed: true, idempotency: 'state', rate: 'write',
  body: strict({ itemId: ItemIdStr }), response: EquipResponse, status: 200,
  errors: ['NOT_FOUND', 'LEVEL_TOO_LOW', 'IN_BATTLE'], spec: 'tech/03 §4', task: 'E05-T02',
});

export const unequip = endpoint({
  id: 'inventory.unequip', method: 'POST', path: '/inventory/unequip', auth: true, signed: true, idempotency: 'state', rate: 'write',
  body: strict({ slot: Slot }), response: EquipResponse, status: 200,
  errors: ['IN_BATTLE'], spec: 'tech/03 §4', task: 'E05-T02',
});

export const dismantle = endpoint({
  id: 'inventory.dismantle', method: 'POST', path: '/inventory/dismantle', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ itemIds: ItemIds }),
  response: strict({ dismantled: z.array(ItemIdStr), scrapGained: z.int().nonnegative(), wallet: Wallet }), status: 200,
  errors: ['ITEM_LOCKED', 'ITEM_EQUIPPED'], spec: 'tech/03 §4; tech/02 §5 (Т-К-41)', task: 'E05-T02',
});

export const lockItem = endpoint({
  id: 'inventory.lock', method: 'POST', path: '/inventory/lock', auth: true, signed: true, idempotency: 'state', rate: 'write',
  body: strict({ itemId: ItemIdStr, locked: z.boolean() }), response: Item, status: 200,
  errors: ['NOT_FOUND'], spec: 'tech/03 §4', task: 'E05-T02',
});

export const upgradeItem = endpoint({
  id: 'inventory.upgrade', method: 'POST', path: '/inventory/upgrade', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ itemId: ItemIdStr }), response: strict({ item: Item, wallet: Wallet }), status: 200,
  errors: ['NOT_FOUND', 'NOT_ENOUGH_NUTS', 'NOT_ENOUGH_SCRAP', 'MAX_LEVEL'], spec: 'tech/03 §4', task: 'E05-T02',
});

/**
 * Продажа скупщику (E05-T14): по списку или пресетом `junk` — «всё обычное и хорошее ниже моего уровня на 5» (не больше
 * 50 за раз; Р-61). Надетое и запертое не продаётся и попадает в `refused`, остальное продаётся.
 */
export const sellItems = endpoint({
  id: 'inventory.sell', method: 'POST', path: '/inventory/sell', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ itemIds: ItemIds.optional(), preset: z.literal('junk').optional() }),
  response: strict({
    sold: z.array(ItemIdStr), nutsGained: z.int().nonnegative(), wallet: Wallet,
    refused: z.array(strict({ itemId: ItemIdStr, code: z.enum(['ITEM_LOCKED', 'ITEM_EQUIPPED']) })).optional(),
  }), status: 200,
  errors: ['ITEM_LOCKED', 'ITEM_EQUIPPED'], spec: 'tech/03 §4', task: 'E05-T02',
});

/** Проданное за 24 ч — для выкупа (ГД 02 §5, Э-К-28; Р-61: в tech/03 списка нет). */
export const getSales = endpoint({
  id: 'inventory.sales', method: 'GET', path: '/inventory/sales', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({ items: z.array(strict({ item: Item, nuts: z.int().nonnegative(), soldAt: IsoTime, buybackUntil: IsoTime })) }), status: 200,
  errors: [], spec: 'tech/03 §4; ГД 02 §5', task: 'E05-T14',
});

/** Выкуп проданного за 24 ч ровно за начисленную сумму, один раз. */
export const buybackItem = endpoint({
  id: 'inventory.buyback', method: 'POST', path: '/inventory/buyback', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ itemId: ItemIdStr }), response: strict({ item: Item, wallet: Wallet }), status: 200,
  errors: ['GONE', 'ALREADY_PURCHASED', 'NOT_ENOUGH_NUTS', 'NOT_FOUND'], spec: 'tech/03 §4; ГД 02 §5', task: 'E05-T14',
});

/** Превью переноса улучшения (ГД 02 §4, economy/02 §4.1). */
export const transferPreview = endpoint({
  id: 'inventory.transfer.preview', method: 'GET', path: '/inventory/transfer/preview', auth: true, signed: false, idempotency: 'read', rate: 'read',
  query: strict({ fromItemId: ItemIdStr, toItemId: ItemIdStr }),
  response: strict({ steps: z.int().nonnegative(), lostSteps: z.int().nonnegative(), masteryMoved: z.int().nonnegative(), costNuts: z.int().nonnegative(), statsAfter: ItemStats }),
  status: 200, errors: ['TRANSFER_NOT_ALLOWED', 'NOT_FOUND'], spec: 'tech/03 §4; ГД 02 §4', task: 'E05-T15',
});

export const transferItem = endpoint({
  id: 'inventory.transfer', method: 'POST', path: '/inventory/transfer', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ fromItemId: ItemIdStr, toItemId: ItemIdStr, expectedCostNuts: z.int().nonnegative() }),
  response: strict({ from: Item, to: Item, wallet: Wallet }), status: 200,
  errors: ['TRANSFER_NOT_ALLOWED', 'NOT_ENOUGH_NUTS', 'STALE_ITEM', 'IN_BATTLE', 'NOT_FOUND'], spec: 'tech/03 §4; economy/02 §4.1', task: 'E05-T15',
});

/** Жетоны председателя (economy/04 §7.6, ГД 02 §6.2). */
export const getS8Tokens = endpoint({
  id: 'sets.s8.tokens', method: 'GET', path: '/sets/s8/tokens', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({
    tokens: z.int().nonnegative(), norm: z.int().positive(),
    exchanges: z.array(strict({ n: z.int().positive(), slot: Slot, itemId: ItemIdStr, weekKey: z.string() })),
    weeklyAvailable: z.boolean(), nextAt: IsoTime.nullable(),
  }), status: 200, errors: [], spec: 'tech/03 §4; economy/04 §7.6', task: 'E05-T17',
});

export const exchangeS8 = endpoint({
  id: 'sets.s8.exchange', method: 'POST', path: '/sets/s8/exchange', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ slot: Slot }), response: strict({ item: Item, wallet: Wallet, exchangesLeft: z.int().nonnegative() }), status: 200,
  errors: ['NOT_ENOUGH_S8_TOKENS', 'WEEKLY_LIMIT', 'LIMIT_REACHED', 'LEVEL_TOO_LOW'], spec: 'tech/03 §4; ГД 02 §6.2', task: 'E05-T17',
});

/** Выпавшее из ящика: предмет, валюта или стопка (Р-61: форма `Drop` в tech/03 не расписана). */
export const Drop = z.union([
  strict({ kind: z.literal('item'), item: Item, trophy: z.boolean().optional() }),
  strict({ kind: z.literal('currency'), currency: CurrencyCode, amount: z.int().positive() }),
  strict({ kind: z.literal('stack'), defId: StackDefId, qty: z.int().positive() }),
]);

export const openBoxes = endpoint({
  id: 'boxes.open', method: 'POST', path: '/boxes/open', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ boxDefId: StackDefId, count: z.int().min(1).max(10) }),
  response: strict({ drops: z.array(Drop), wallet: Wallet, stacks: z.array(Stack), oddsVersion: z.int().nonnegative() }), status: 200,
  errors: ['NOT_ENOUGH_KEYS', 'NOT_FOUND', 'LIMIT_INVALID'], spec: 'tech/03 §4; economy/04 §1–§2', task: 'E05-T03',
});

/** Публичные шансы (канон §2, A19): из той же таблицы `drops.*`, что и розыгрыш; трофеи — отдельной строкой. */
export const getBoxOdds = endpoint({
  id: 'boxes.odds', method: 'GET', path: '/boxes/{boxDefId}/odds', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: strict({ boxDefId: z.union([StackDefId, z.string().regex(/^(B[0-9]{2,3}|W[0-9]{2})$/)]) }),
  response: strict({
    oddsVersion: z.int().nonnegative(),
    table: z.array(strict({ rarity: Rarity, chance: z.number().min(0).max(1) })),
    items: z.array(strict({ defId: z.union([ItemDefId, StackDefId]), chance: z.number().min(0).max(1), trophy: z.boolean().optional(), currency: CurrencyCode.optional() })),
  }), status: 200, errors: ['NOT_FOUND'], spec: 'tech/03 §4; канон §2', task: 'E05-T03',
});
